import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown, ExternalLink, BadgeCheck, Briefcase, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const NAV_ITEMS = [
  { label: "Home", href: "#home", type: "scroll" },
  { label: "About", href: "#about", type: "scroll" },
  { label: "Products", href: "/products", type: "link" },
  { label: "Gallery", href: "/gallery", type: "link" },
  { label: "Team", href: "/team", type: "link" },
  { label: "Pricing", href: "#pricing", type: "scroll" },
  { label: "Contact", href: "#contact", type: "scroll" },
];

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForm, setApplyForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    message: "",
  });
  const [resumeName, setResumeName] = useState("");
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogoClick = (e) => {
    if (isHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNavClick = (e, item) => {
    if (item.type === "scroll") {
      e.preventDefault();
      if (!isHome) {
        window.location.href = `/${item.href}`;
        return;
      }
      const el = document.querySelector(item.href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    const lines = [
      "*Job Application - Prime Technology*",
      "",
      `*Name:* ${applyForm.name}`,
      `*Email:* ${applyForm.email}`,
      `*Phone:* ${applyForm.phone}`,
      `*Position:* ${applyForm.position}`,
      "",
      "*Message:*",
      applyForm.message,
      "",
      resumeName ? `*Resume:* ${resumeName}` : "*Resume:* Not attached yet",
      "",
      "Please reply with your resume PDF in this chat.",
    ].join("\n");

    const waURL = `https://wa.me/918090773315?text=${encodeURIComponent(lines)}`;
    window.open(waURL, "_blank");
    setApplyOpen(false);
    setApplyForm({ name: "", email: "", phone: "", position: "", message: "" });
    setResumeName("");
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled ? "py-2" : "py-4"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={cn(
            "flex items-center justify-between rounded-2xl transition-all duration-500",
            scrolled
              ? "bg-[#0D47A1]/95 border border-white/10 shadow-[0_10px_40px_rgba(13,71,161,0.3)] px-5 py-3"
              : "bg-[#0D47A1] px-5 py-4"
          )}
        >
          {/* Logo */}
          <Link
            to="/"
            onClick={handleLogoClick}
            data-testid="navbar-logo"
            className="flex items-center"
          >
            <div className="relative group">
              <div className="absolute -inset-1.5 rounded-xl bg-gradient-to-r from-[#FFA000]/30 via-[#FFA000]/10 to-[#2196F3]/20 opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500" />
              <div className="relative rounded-xl bg-white px-3 py-1.5 shadow-[0_4px_20px_rgba(255,255,255,0.15)] ring-1 ring-white/20">
                <img src="/logo.png" alt="Prime Technology" className="h-8 md:h-10 object-contain" />
              </div>
            </div>
          </Link>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.type === "link" ? item.href : item.href}
                onClick={(e) => handleNavClick(e, item)}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`px-4 py-2 text-[14px] font-medium text-white/85 hover:text-white transition-colors rounded-lg hover:bg-white/10 ${
                  item.type === "link" ? "flex items-center gap-1" : ""
                }`}
              >
                {item.label}
                {item.type === "link" && <ExternalLink className="w-3 h-3 opacity-50" />}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => scrollTo("#contact")}
              data-testid="navbar-cta-demo"
              className="hidden md:inline-flex btn-magnetic rounded-lg bg-[#FFA000] hover:bg-[#E69000] text-white px-6 h-11 text-[14px] font-bold shadow-[0_4px_15px_rgba(255,160,0,0.4)]"
            >
              Download Now
              <ChevronRight className="w-4 h-4 ml-0.5" />
            </Button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="navbar-mobile-toggle"
              className="lg:hidden w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>
      </div>

      {/* Partner logos strip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-2 hidden lg:block">
        <div className="flex items-center justify-between rounded-xl bg-white/95 border border-[#0D47A1]/10 px-5 py-2 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0D47A1]/10 border border-[#0D47A1]/20 px-3 py-1">
              <BadgeCheck className="w-3.5 h-3.5 text-[#0D47A1]" />
              <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#0D47A1]">Authorized Tally certified Partner</span>
            </div>
            <div className="inline-flex items-center gap-2 text-[#1A1A2E]">
              <Briefcase className="w-4 h-4 text-[#0D47A1]" />
              <span className="text-[12px] font-semibold">We are hiring!</span>
              <Button
                onClick={() => setApplyOpen(true)}
                size="sm"
                className="h-7 px-3 text-[11px] font-bold bg-[#F4B942] hover:bg-[#E6A832] text-[#1A1A1A] rounded-md"
              >
                Apply Now
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-[11px] font-semibold tracking-wide text-[#4A5568]">Powered by</span>
            <img src="/tally-prime.png" alt="TallyPrime" className="h-7 object-contain" />
            <img src="/tally-capital.png" alt="TallyCapital" className="h-7 object-contain" />
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden max-w-7xl mx-auto px-6 mt-2"
          >
            <div className="bg-[#0D47A1] border border-white/10 rounded-2xl p-4 shadow-[0_20px_60px_rgba(13,71,161,0.4)]">
              <div className="grid gap-1">
                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      handleNavClick(e, item);
                      if (item.type === "scroll" && isHome) setOpen(false);
                      if (item.type === "link") setOpen(false);
                    }}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="px-4 py-3 rounded-lg text-[15px] font-medium text-white hover:bg-white/10 flex items-center justify-between"
                  >
                    {item.label}
                    <ChevronRight className="w-4 h-4 text-white/50" />
                  </a>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-[13px] text-white/80 mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[#FFA000]" /> We are hiring!
                </p>
                <Button
                  onClick={() => {
                    setApplyOpen(true);
                    setOpen(false);
                  }}
                  className="w-full rounded-lg bg-[#F4B942] hover:bg-[#E6A832] text-[#1A1A1A] h-11 font-bold"
                >
                  Apply Now
                </Button>
              </div>
              <Button
                onClick={() => {
                  scrollTo("#contact");
                  setOpen(false);
                }}
                data-testid="mobile-cta-demo"
                className="mt-3 w-full rounded-lg bg-[#FFA000] hover:bg-[#E69000] text-white h-12 font-bold"
              >
                Download Now
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Job Application Modal */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Join Our Team</DialogTitle>
            <DialogDescription>
              Apply for a position at Prime Technology. We'll open a pre-filled WhatsApp message for you.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleApplySubmit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="apply-name">Full Name</Label>
              <Input
                id="apply-name"
                required
                value={applyForm.name}
                onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="apply-email">Email</Label>
              <Input
                id="apply-email"
                type="email"
                required
                value={applyForm.email}
                onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="apply-phone">Phone</Label>
              <Input
                id="apply-phone"
                type="tel"
                required
                value={applyForm.phone}
                onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })}
                placeholder="+91 98765 43210"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="apply-position">Position</Label>
              <Input
                id="apply-position"
                required
                value={applyForm.position}
                onChange={(e) => setApplyForm({ ...applyForm, position: e.target.value })}
                placeholder="Tally Implementation Consultant"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="apply-message">Message / Cover Note</Label>
              <Textarea
                id="apply-message"
                rows={3}
                value={applyForm.message}
                onChange={(e) => setApplyForm({ ...applyForm, message: e.target.value })}
                placeholder="Tell us why you're a good fit..."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="apply-resume">Resume (PDF)</Label>
              <div className="relative">
                <Input
                  id="apply-resume"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setResumeName(e.target.files?.[0]?.name || "")}
                  className="file:hidden py-2 cursor-pointer"
                />
                <div className="absolute inset-0 flex items-center gap-2 px-3 pointer-events-none">
                  <Upload className="w-4 h-4 text-[#0D47A1]" />
                  <span className="text-sm text-[#4A5568] truncate">
                    {resumeName || "Choose PDF file"}
                  </span>
                </div>
              </div>
              <p className="text-[11px] text-[#4A5568]">
                Note: WhatsApp web links cannot attach files. After submitting, please send your resume PDF in the same chat.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#0D47A1] hover:bg-[#0A3680] text-white font-bold h-11"
            >
              Submit via WhatsApp
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </motion.header>
  );
}
